import { debounce, takeLatest, select, put, call, race, take, delay } from 'redux-saga/effects';

import { updateUrlQuery } from '../utils';
import {
  CLEAR_SITE_DATA,
  SET_ACTIVE_COMPONENT_ID,
  RECEIVE_ACTIVE_COMPONENT,
  RECEIVE_POLLING_DATE_RANGE,
  REFRESH_VALUES,
  RECEIVE_REFRESH,
  INITIALIZE_POLLING,
  START_POLLING,
  STOP_POLLING,
  refreshData,
} from './actions';
import { getPollingInterval } from './selectors';

let lastRequestedPollingAt = 0;

// Wraps a site-scoped worker so a response arriving after CLEAR_SITE_DATA can't repopulate the store.
export const afterSiteCleared = (worker) =>
  function* afterSiteClearedWorker(action) {
    yield race([call(worker, action), take(CLEAR_SITE_DATA)]);
  };

export function* doSetActiveComponentId(action) {
  const activeComponentId = yield select((state) => state.application.activeComponentId);
  // Resets to actual root component (null) if 'selecting' already existing root
  yield put({
    type: RECEIVE_ACTIVE_COMPONENT,
    activeComponentId: action.id === activeComponentId ? null : action.id,
  });

  updateUrlQuery({
    activeComponent: !action.id || action.id === activeComponentId ? undefined : action.id,
  });
}

export function doReceivePollingDateRange(action) {
  const {
    pollingDateRange: { startDate: start, endDate: end },
    panelId,
  } = action;

  if (!panelId) {
    updateUrlQuery({
      startDate: start || undefined,
      endDate: end || undefined,
    });
  }
}

export function* doReceiveRefresh() {
  yield put({
    type: RECEIVE_REFRESH,
    refreshedAt: Date.now(),
  });
}

// isInitialStart: the first start of a site visit, whose refresh duplicates the panels' own initial fetches.
export function* doPoll(siteId, isInitialStart = false) {
  let isFirstTick = isInitialStart;
  while (true) {
    const pollingInterval = yield select(getPollingInterval);
    const now = Date.now();
    if (pollingInterval && lastRequestedPollingAt + pollingInterval < now) {
      lastRequestedPollingAt = Date.now();
      yield put(refreshData(siteId, isFirstTick));
    }
    isFirstTick = false;
    yield delay(1000);
  }
}

export function* doInitializePolling(action) {
  let isInitialStart = true;
  while (true) {
    yield take(START_POLLING);
    yield race([call(doPoll, action.siteId, isInitialStart), take(STOP_POLLING)]);
    isInitialStart = false;
  }
}

export const sagas = [
  takeLatest(INITIALIZE_POLLING, doInitializePolling),
  takeLatest(SET_ACTIVE_COMPONENT_ID, doSetActiveComponentId),
  takeLatest(RECEIVE_POLLING_DATE_RANGE, doReceivePollingDateRange),

  debounce(500, REFRESH_VALUES, doReceiveRefresh),
];
